<script setup>
import { formattedDate, swalError, swalSuccess } from '@/helper';
import { get_tickets, update_ticket } from '@/services/tickets';
import { onMounted, ref } from 'vue';


const tickets = ref([])
const isLoading = ref(true)
onMounted(async()=>{
  await retrieve_tickets()
  isLoading.value = false
})

async function retrieve_tickets(){
  let response = await get_tickets('support_ticket')
  if (response.error){
    // Don't show error if it's a 403 redirect (user will be redirected)
    if (!response.isRedirecting) {
      swalError(response.message || 'Failed to load tickets')
    }
    return
  }
  tickets.value = response.result?.data?.data || []
}

const filter = ref({
  value: 9
})

async function archiveTicket(ticket){
  let params = {
    is_archive: 1
  }
  let response = await update_ticket(ticket.id, params)
  if(response.error){
    swalError(response.result, true)
    return
  }
  ticket.attributes.is_archive = 1
  swalSuccess("Ticket has been archive")
}

function getText(status){
  let text = ''
  switch (status) {
    case 'on_hold':
      text = 'On Hold'
      break;
    case '':
      text = 'Open'
      break;
    case 'closed':
      text = 'Closed'
      break;
    default:
      text = 'Open'
      break;
  }
  return text
}
</script>

<template>
  <Breadcrumbs main="Support Ticket"/>
  
  <div class="container-fluid">
    <div class="row">
      <!-- Page Title -->
      <div class="card">
        <div class="card-body">
          <div class="d-flex align-items-center justify-content-between">
            <h4 class="mb-0">Support Tickets</h4>
          </div>
        </div>
      </div>
      
      <div class="d-flex mb-4">
        <button class="btn rounded-pill btn-light-primary me-2"
          @click="filter.value = 9"
          :class="filter.value == 9 ? 'active' : ''"
        >
          All
        </button>
        <button class="btn rounded-pill btn-light-primary me-2"
          @click="filter.value = 0"
          :class="filter.value == 0 ? 'active' : ''"
        >
          Not Archived
        </button>
        <button class="btn rounded-pill btn-light-primary"
          @click="filter.value = 1"
          :class="filter.value == 1 ? 'active' : ''"
        >
          Archived
        </button>
      </div>

      <!-- Item List -->
      <template v-if="isLoading">
        <loadingCard />
      </template>
      <template v-else>
        <template v-if="tickets == 0">
          <emptyCard />
        </template>
        <template v-else>
          <div class="col-md-12 mb-2">
            <div class="card mb-1 rounded-0">
              <div class="card-body py-1">
                <div class="row">
                  <div class="col-5 my-auto">
                    <p class="text-truncate">
                      Title
                    </p>
                  </div>
                  <div class="col-3">
                    <p class="text-center">Status</p>
                  </div>
                  <div class="col-2 d-flex justify-content-end align-items-center">
                    <p class="text-center">Action</p>
                  </div>
                  <div class="col-2 d-flex justify-content-start align-items-center">
                    <p class="text-center">Date Created</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <template  v-for="ticket in tickets" :key="ticket.id">
            <div v-auto-animate v-if="ticket.attributes.is_archive == filter.value" class="mb-1">
              <div class="col-md-12">
                <div class="card mb-1">
                  <div class="card-body py-3">
                    <div class="row">
                      <div class="col-5 my-auto">
                        <small>{{ ticket.attributes.topic }}</small>
                        <p class="text-truncate">
                          {{ ticket.attributes.subject }}
                        </p>
                      </div>
                      <div class="col-3">
                        <div class="rounded-pill text-center p-2"
                          :class="ticket.attributes.status"
                        >
                          <p>{{ getText(ticket.attributes.status) }}</p>
                        </div>
                      </div>
                      <div class="col-2 d-flex justify-content-end align-items-center">
                        <button class="btn btn-light-danger icon-btn"
                          @click="archiveTicket(ticket)"
                          :class="ticket.attributes.is_archive ? 'disabled' : ''"
                        >
                          <vue-feather type="archive" size="1rem" />
                        </button>
                        <router-link class="btn btn-light-success icon-btn ms-1"
                          :to="{name: 'support-view', params: {id: ticket.id}}"
                        >
                          <vue-feather type="chevron-right" size="1rem" />
                        </router-link>
                      </div>
                      <div class="col-2 d-flex justify-content-start align-items-center">
                        <p class="text-center">{{ formattedDate(ticket.attributes.created_at) }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-auto-animate v-if="filter.value == 9" class="mb-1">
              <div class="col-md-12">
                <div class="card mb-1">
                  <div class="card-body py-3">
                    <div class="row">
                      <div class="col-5 my-auto">
                        <small>{{ ticket.attributes.topic }}</small>
                        <p class="text-truncate">
                          {{ ticket.attributes.subject }}
                        </p>
                      </div>
                      <div class="col-3">
                        <div class="rounded-pill bg-light-success text-center p-2"
                          :class="ticket.attributes.status"
                        >
                          <p>{{ getText(ticket.attributes.status) }}</p>
                        </div>
                      </div>
                      <div class="col-2 d-flex justify-content-end align-items-center">
                        <button class="btn btn-light-danger icon-btn"
                          v-if="!ticket.attributes.is_archive"
                          @click="archiveTicket(ticket)"
                        >
                          <vue-feather type="archive" size="1rem" />
                        </button>
                        <router-link class="btn btn-light-success icon-btn ms-1"
                          :to="{name: 'support-view', params: {id: ticket.id}}"
                        >
                          <vue-feather type="chevron-right" size="1rem" />
                        </router-link>
                      </div>
                      <div class="col-2 d-flex justify-content-start align-items-center">
                        <p class="text-center">{{ formattedDate(ticket.attributes.created_at) }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </template>
      </template>
    </div>
  </div>
</template>

<style scoped>
  .on_hold{
    background-color: rgba(245, 190, 101, 0.2) !important;
    border: 1px solid #F5BE65 !important;
    color: #F5BE65;
  }
  .closed{
    background-color: #fcf6f6 !important;
    border: 1px solid #FC4438;
    color: #FC4438 !important;
  }
</style>